import { Box, Container, Stack } from '@mui/material';
import {
  AgronodAlert,
  AgronodCard,
  AgronodIcon,
  AgronodTypography,
  LoaderLinear,
} from '@agronod/mui-components';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { TillbakaLank } from '../../components/TillbakaLank';
import { useToken } from '../../utils/hooks';
import {
  ApiError,
  grupperaPerModell,
  INRIKTNING_LABEL,
  mattKolumner,
  verifieringApi,
} from '../../utils/api';
import { revisionKeys, useRevisionScope, useRollForKund } from './revisionScope';
import { GardTabell } from './GardTabell';
import { IntervallFotnot, SektionRubrik, StatusForklaring } from './StatusForklaring';

/**
 * De avpseudonymiserade gårdarna (kravspec v4 C.6): revisorernas enda vy,
 * och revisionsledarens sammanställning efter låsning. Tom tills
 * revisionsledaren avpseudonymiserat. Bygger på persisterat tillstånd och
 * bevaras efter åtkomstperioden (C.7).
 */
const RevisionRevealedPage = () => {
  const { scope, basePath } = useRevisionScope();
  const { accessToken } = useToken();
  const {
    kundNamn,
    dataskyddGodkant,
    isPending: rollPending,
  } = useRollForKund(scope?.kundId);

  const { data, isPending, error } = useQuery({
    queryKey: scope ? revisionKeys.revealed(scope) : ['revision-revealed'],
    queryFn: () => verifieringApi.getRevisionRevealed(accessToken!, scope!),
    enabled: !!accessToken && !!scope && dataskyddGodkant,
    retry: false,
  });

  if (!scope) {
    return <Navigate to="/" />;
  }
  // AS-4231: dataskyddsvillkoren godkänns i kundvalet innan kunden öppnas.
  if (!rollPending && !dataskyddGodkant) {
    return <Navigate to="/" replace />;
  }
  if (!accessToken || isPending || rollPending) {
    return <LoaderLinear color="primary" isGlobal={true} />;
  }

  const arLedare = data?.roll === 'REVISIONSLEDARE' && data.identitetVisas;
  const sektioner = grupperaPerModell(data?.gardar ?? []).filter(
    (s) => s.gardar.length > 0,
  );
  const tillbaka = (
    <TillbakaLank to={arLedare ? basePath : '/'}>
      {arLedare
        ? 'Tillbaka till revisionslistan'
        : 'Tillbaka till alla verifieringsuppdrag'}
    </TillbakaLank>
  );

  if (error) {
    const severity =
      error instanceof ApiError && error.status === 403 ? 'info' : 'error';
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {tillbaka}
        <AgronodAlert severity={severity}>{error.message}</AgronodAlert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 8 }}>
      {tillbaka}
      <Box sx={{ mb: 3 }}>
        <AgronodTypography variant="overline" sx={{ color: 'text.secondary' }}>
          {kundNamn}
        </AgronodTypography>
        <AgronodTypography variant="h4" sx={{ mb: 1 }}>
          Revision {INRIKTNING_LABEL[scope.inriktning].toLowerCase()} {scope.ar}
        </AgronodTypography>
        <AgronodTypography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {data.gardar.length === 0 ? (
            'Urvalet görs av revisionsledaren.'
          ) : (
            <>
              Gårdar utvalda för revision.{' '}
              {data.identitetVisas
                ? 'Identitet visas eftersom revisionsledaren avpseudonymiserat urvalet;'
                : 'Detta uppdrag visar urvalet utan identitet – gårdarna visas med sina pseudonymnummer;'}{' '}
              för varje nyckeltal syns bara om gården ligger inom, under eller
              över förväntat intervall, inga underliggande värden.
            </>
          )}
        </AgronodTypography>
      </Box>

      {data.gardar.length === 0 ? (
        // Tomt-läge: revisionsledaren har inte avpseudonymiserat urvalet än.
        <AgronodCard sx={{ px: 4, py: 8 }}>
          <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Box sx={{ color: 'icon.secondary', display: 'inline-flex' }}>
              <AgronodIcon name="loadingSpinnerStatic" fontSize="large" />
            </Box>
            <AgronodTypography variant="h5" component="h2">
              Här visas gårdarna när urvalet är klart
            </AgronodTypography>
            <AgronodTypography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 460 }}
            >
              Revisionsledaren har ännu inte valt ut vilka gårdar som ska
              revideras.{' '}
              {data.lastAntal !== null && `Antalet är bestämt till ${data.lastAntal} gårdar. `}
              När urvalet är bekräftat visas gårdarna här
              {data.identitetVisas ? ' med namn och organisationsnummer' : ''}.
            </AgronodTypography>
          </Stack>
        </AgronodCard>
      ) : (
        <Stack spacing={3}>
          <StatusForklaring />
          {sektioner.map((sektion) => (
            <Box key={sektion.titel}>
              <SektionRubrik
                titel={`${sektion.titel} (${sektion.gardar.length})`}
                meta="Klicka på en gård för att öppna detaljvyn"
              />
              <GardTabell
                sektion={sektion}
                kolumner={
                  sektion.modell
                    ? mattKolumner(
                        sektion.gardar,
                        sektion.modell,
                        data.modeller.find((m) => m.modell === sektion.modell)?.matt,
                      )
                    : []
                }
                detaljUrl={(nummer) =>
                  `${basePath}/avpseudonymiserade/gard/${nummer}`
                }
              />
            </Box>
          ))}
          <IntervallFotnot />
        </Stack>
      )}
    </Container>
  );
};

export default RevisionRevealedPage;
